import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  DogVaccination,
  CreateDogVaccinationInput,
} from "../lib/api";

function getExpirationStatus(
  expirationDate: string | null
): "expired" | "expiring" | "valid" | null {
  if (!expirationDate) return null;

  const now = new Date();
  const expDate = new Date(expirationDate);
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  if (expDate < now) return "expired";
  if (expDate <= thirtyDaysFromNow) return "expiring";
  return "valid";
}

interface ExpirationBadgeProps {
  status: "expired" | "expiring" | "valid" | null;
}

function ExpirationBadge({ status }: ExpirationBadgeProps) {
  if (!status || status === "valid") return null;

  const styles = {
    expired: "bg-red-100 text-red-700",
    expiring: "bg-yellow-100 text-yellow-700",
  };

  const labels = {
    expired: "Expired",
    expiring: "Expiring Soon",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

interface VaccinationCardProps {
  vaccination: DogVaccination;
  onDelete: () => void;
  isDeleting: boolean;
}

function VaccinationCard({
  vaccination,
  onDelete,
  isDeleting,
}: VaccinationCardProps) {
  const expirationStatus = getExpirationStatus(vaccination.expirationDate);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900">
              {vaccination.vaccinationType}
            </p>
            <ExpirationBadge status={expirationStatus} />
          </div>
          <div className="text-sm text-gray-500 mt-1">
            <p>
              Administered:{" "}
              {new Date(vaccination.administeredDate).toLocaleDateString()}
            </p>
            {vaccination.expirationDate && (
              <p>
                Expires:{" "}
                {new Date(vaccination.expirationDate).toLocaleDateString()}
              </p>
            )}
            {vaccination.veterinarian && (
              <p>Veterinarian: {vaccination.veterinarian}</p>
            )}
          </div>
          {vaccination.notes && (
            <p className="text-sm text-gray-600 mt-1">{vaccination.notes}</p>
          )}
        </div>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50"
        >
          {isDeleting ? "..." : "Delete"}
        </button>
      </div>
    </div>
  );
}

interface AddVaccinationModalProps {
  dogId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function AddVaccinationModal({
  dogId,
  onClose,
  onSuccess,
}: AddVaccinationModalProps) {
  const [vaccinationType, setVaccinationType] = useState("");
  const [administeredDate, setAdministeredDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [veterinarian, setVeterinarian] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateDogVaccinationInput) =>
      api.vaccinations.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to add vaccination");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!vaccinationType.trim()) {
      setError("Vaccination type is required");
      return;
    }

    if (!administeredDate) {
      setError("Date administered is required");
      return;
    }

    createMutation.mutate({
      dogId,
      vaccinationType: vaccinationType.trim(),
      administeredDate,
      expirationDate: expirationDate || null,
      veterinarian: veterinarian.trim() || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Add Vaccination</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="vaccinationType"
              className="block text-sm font-medium text-gray-700"
            >
              Vaccination Type *
            </label>
            <input
              id="vaccinationType"
              type="text"
              value={vaccinationType}
              onChange={(e) => setVaccinationType(e.target.value)}
              placeholder="e.g., Rabies, DHPP, Bordetella"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="administeredDate"
              className="block text-sm font-medium text-gray-700"
            >
              Date Administered *
            </label>
            <input
              id="administeredDate"
              type="date"
              value={administeredDate}
              onChange={(e) => setAdministeredDate(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="expirationDate"
              className="block text-sm font-medium text-gray-700"
            >
              Expiration Date
            </label>
            <input
              id="expirationDate"
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          <div>
            <label
              htmlFor="veterinarian"
              className="block text-sm font-medium text-gray-700"
            >
              Veterinarian Name
            </label>
            <input
              id="veterinarian"
              type="text"
              value={veterinarian}
              onChange={(e) => setVeterinarian(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          <div>
            <label
              htmlFor="vaccinationNotes"
              className="block text-sm font-medium text-gray-700"
            >
              Notes
            </label>
            <textarea
              id="vaccinationNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? "Adding..." : "Add Vaccination"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  vaccinationType: string;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

function DeleteConfirmModal({
  vaccinationType,
  onClose,
  onConfirm,
  isDeleting,
}: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Delete Vaccination?</h2>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete the {vaccinationType} vaccination
          record? This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface DogVaccinationsProps {
  dogId: string;
  dogName: string;
}

export function DogVaccinations({ dogId, dogName: _dogName }: DogVaccinationsProps) {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingVaccination, setDeletingVaccination] =
    useState<DogVaccination | null>(null);

  // Fetch vaccinations for this dog
  const { data: vaccinationsData, isLoading } = useQuery({
    queryKey: ["vaccinations", dogId],
    queryFn: () => api.vaccinations.list({ dogId }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.vaccinations.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vaccinations", dogId] });
      setDeletingVaccination(null);
    },
  });

  const handleAddSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["vaccinations", dogId] });
  };

  const vaccinations = vaccinationsData?.data ?? [];

  if (isLoading) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-sm text-gray-500 animate-pulse">
          Loading vaccinations...
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700">Vaccinations</h4>
        <button
          onClick={() => setShowAddModal(true)}
          className="text-xs text-amber-600 hover:text-amber-700 font-medium"
        >
          + Add Vaccination
        </button>
      </div>

      {vaccinations.length === 0 ? (
        <p className="text-sm text-gray-500">No vaccinations recorded.</p>
      ) : (
        <div className="space-y-2">
          {vaccinations.map((vaccination) => (
            <VaccinationCard
              key={vaccination.id}
              vaccination={vaccination}
              onDelete={() => setDeletingVaccination(vaccination)}
              isDeleting={
                deletingVaccination?.id === vaccination.id &&
                deleteMutation.isPending
              }
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddVaccinationModal
          dogId={dogId}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
        />
      )}

      {deletingVaccination && (
        <DeleteConfirmModal
          vaccinationType={deletingVaccination.vaccinationType}
          onClose={() => setDeletingVaccination(null)}
          onConfirm={() => deleteMutation.mutate(deletingVaccination.id)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
